"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

// Wingo UI Pro · https://wingo-ui.com/legal/license

import {
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Slot } from "@radix-ui/react-slot";
import { ArrowRight, Check, ChevronLeft, ChevronRight, ChevronsUpDown, MoreHorizontal } from "lucide-react";
import { AnimatePresence, motion, type TargetAndTransition } from "motion/react";
import { Button } from "@/components/wingo/ui/button";
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuRoot, DropdownMenuTrigger } from "@/components/wingo/ui/dropdown-menu";
import { Tooltip } from "@/components/wingo/ui/tooltip";
import { useOverflowItems, type OverflowItemProps } from "@/components/wingo/hooks/use-overflow-items";
import { durations, springs, usePrefersReducedMotion } from "@/components/wingo/lib/motion";
import {
  NavLink,
  navItemKey,
  navItemText,
  normalizePath,
  type LinkComponent,
  type NavItem,
  type NavLinkProps,
} from "@/components/wingo/lib/navigation";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";
import { useDefaults } from "@/components/wingo/lib/ui-config";

export type BreadcrumbSeparatorKind = "chevron" | "slash" | "dot" | "arrow";
export type BreadcrumbSize = "sm" | "md";
export type BreadcrumbCollapse = "auto" | "never";
export type BreadcrumbMobileVariant = "back" | "collapse" | "full";
export type BreadcrumbMobileBreakpoint = "sm" | "md" | "always";
export type BreadcrumbSlot = "list" | "item" | "link" | "page" | "separator" | "ellipsis" | "back";

export type BreadcrumbEntry = {
  // stable key; default href, else the text
  id?: string;
  // empty with an icon renders an icon-only crumb, named by textValue (screen readers and a tooltip)
  label: ReactNode;
  href?: string;
  icon?: ReactNode;
  // runs on click, before navigation; entries without href but with onSelect render a button
  onSelect?: (event: MouseEvent<HTMLElement>) => void;
  // siblings: a chevron after the label opens a menu of them, the current one checked
  menu?: NavItem[];
  // plain text for tooltips, menus and aria when label is not a string
  textValue?: string;
};

export type BreadcrumbLabels = {
  navigation?: string;
  more?: string;
  back?: (label: string) => string;
  siblings?: (label: string) => string;
  // the Cancel row of the phone action sheets
  cancel?: string;
};

const DEFAULT_LABELS: Required<BreadcrumbLabels> = {
  navigation: "Breadcrumb",
  more: "Show hidden pages",
  back: (label) => `Back to ${label}`,
  siblings: (label) => `Switch ${label}`,
  cancel: "Cancel",
};

// ---------------------------------------------------------------- classes

const LIST_SIZES: Record<BreadcrumbSize, string> = {
  sm: "gap-1 text-[13px]",
  md: "gap-1.5 text-sm",
};

const ITEM_SIZES: Record<BreadcrumbSize, string> = {
  sm: "gap-1",
  md: "gap-1.5",
};

// icon to label inside a crumb
const INNER_GAP: Record<BreadcrumbSize, string> = {
  sm: "gap-1",
  md: "gap-1.5",
};

const FOCUS =
  "outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline focus-visible:outline-ring";

// padded into the gap (-mx-1 px-1) so the hover and focus area is bigger without moving the text; 44px tall on touch whatever the text size
const LINK =
  "relative -mx-1 inline-flex min-w-0 cursor-pointer touch-manipulation select-none items-center rounded-[6px] px-1 py-0.5 text-muted-foreground [-webkit-tap-highlight-color:transparent] transition-colors duration-fast ease-smooth hover:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:top-1/2 pointer-coarse:after:h-11 pointer-coarse:after:-translate-y-1/2";

const PAGE = "inline-flex min-w-0 items-center font-medium text-foreground";

const ICON = "flex shrink-0 items-center [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5";

const LABEL = "min-w-0 truncate";

const SEPARATOR = "flex shrink-0 items-center text-foreground/30 select-none [&_svg:not([class*='size-'])]:size-3.5";

const SWITCHER =
  "relative -ms-0.5 -me-1 inline-flex size-5 shrink-0 cursor-pointer touch-manipulation select-none items-center justify-center rounded-[6px] text-muted-foreground [-webkit-tap-highlight-color:transparent] transition-colors duration-fast ease-smooth hover:bg-foreground/5 hover:text-foreground data-[state=open]:bg-foreground/10 data-[state=open]:text-foreground pointer-coarse:after:absolute pointer-coarse:after:-inset-3";

const ELLIPSIS_SIZES: Record<BreadcrumbSize, string> = {
  sm: "-mx-0.5 -my-1 size-6 pointer-coarse:after:-inset-2.5",
  md: "-mx-1 -my-1 size-7 pointer-coarse:after:-inset-2",
};

const BACK =
  "relative inline-flex min-h-11 max-w-full min-w-0 cursor-pointer touch-manipulation select-none items-center gap-1 rounded-[8px] text-[15px] font-medium text-foreground [-webkit-tap-highlight-color:transparent] transition-opacity duration-fast ease-smooth active:opacity-60";

// the desktop trail and the phone form, both rendered and switched by css so the server html is right on a phone
const BREAKPOINTS: Record<BreadcrumbMobileBreakpoint, { trail: string; phone: string }> = {
  sm: { trail: "max-sm:hidden", phone: "sm:hidden" },
  md: { trail: "max-md:hidden", phone: "md:hidden" },
  always: { trail: "hidden", phone: "" },
};

// ---------------------------------------------------------------- context

type BreadcrumbContextValue = {
  size: BreadcrumbSize;
  separator: BreadcrumbSeparatorKind | ReactNode;
  linkComponent?: LinkComponent;
  labels: Required<BreadcrumbLabels>;
  classNames?: Partial<Record<BreadcrumbSlot, string>>;
  // menus become bottom action sheets below 768px
  responsive: boolean;
};

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  size: "md",
  separator: "chevron",
  labels: DEFAULT_LABELS,
  responsive: true,
});

const SIZES: BreadcrumbSize[] = ["sm", "md"];
const safeSize = (size: unknown): BreadcrumbSize => (SIZES.includes(size as BreadcrumbSize) ? (size as BreadcrumbSize) : "md");

const filled = (node: ReactNode) => node !== undefined && node !== null && node !== false && node !== "";

// the text of an entry for tooltips, menus and aria
function entryText(entry: BreadcrumbEntry): string {
  if (entry.textValue) return entry.textValue;
  return typeof entry.label === "string" || typeof entry.label === "number" ? String(entry.label) : "";
}

function entryKeys(entries: readonly BreadcrumbEntry[]): string[] {
  const seen = new Map<string, number>();
  return entries.map((entry, i) => {
    const base = entry.id ?? entry.href ?? (entryText(entry) || `entry-${i}`);
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count ? `${base}#${count}` : base;
  });
}

// a menu whose open state belongs to one trail: when the entries change while it is open, it closes
function useTrailMenu(trailKey: string) {
  const [openFor, setOpenFor] = useState<string | null>(null);
  return [openFor === trailKey, (open: boolean) => setOpenFor(open ? trailKey : null)] as const;
}

// ---------------------------------------------------------------- parts

function SeparatorGlyph({ kind }: { kind: BreadcrumbSeparatorKind | ReactNode }) {
  if (kind === "chevron" || kind === undefined || kind === null) return <ChevronRight className="rtl:rotate-180" />;
  if (kind === "arrow") return <ArrowRight className="rtl:rotate-180" />;
  if (kind === "slash") return <span className="text-[1.05em] leading-none">/</span>;
  if (kind === "dot") return <span className="text-[1.2em] leading-none font-bold">·</span>;
  return <>{kind}</>;
}

function listClass({ size, classNames }: Pick<BreadcrumbContextValue, "size" | "classNames">) {
  return cn("flex min-w-0 flex-wrap items-center text-muted-foreground", LIST_SIZES[size], classNames?.list);
}

// the data-driven rows: the caller's list classes still win over the fold layout
function trailListClass(context: Pick<BreadcrumbContextValue, "size" | "classNames">, fold: boolean, className?: string) {
  return cn(
    listClass({ size: context.size }),
    "relative",
    // room for focus outlines inside the clipped row
    fold && "-mx-2 -my-1.5 flex-nowrap overflow-hidden px-2 py-1.5",
    context.classNames?.list,
    className,
  );
}

export type BreadcrumbListProps = ComponentProps<"ol">;

function BreadcrumbListRender({ className, ...props }: BreadcrumbListProps) {
  const context = useContext(BreadcrumbContext);
  return <ol data-slot="breadcrumb-list" className={cn(listClass(context), className)} {...props} />;
}

export type BreadcrumbItemProps = ComponentProps<"li">;

function BreadcrumbItemRender({ className, ...props }: BreadcrumbItemProps) {
  const { size, classNames } = useContext(BreadcrumbContext);
  return <li data-slot="breadcrumb-item" className={cn("inline-flex min-w-0 items-center", ITEM_SIZES[size], classNames?.item, className)} {...props} />;
}

export type BreadcrumbLinkProps = Omit<NavLinkProps, "href" | "component"> & {
  // renders NavLink; without href (and without asChild) it is a button that runs onClick
  href?: string;
  // renders the single child (a router Link) with the crumb look
  asChild?: boolean;
  linkComponent?: LinkComponent;
};

function BreadcrumbLinkRender({ href, asChild = false, linkComponent, className, children, ...props }: BreadcrumbLinkProps) {
  const context = useContext(BreadcrumbContext);
  const classes = cn(LINK, FOCUS, INNER_GAP[context.size], context.classNames?.link, className);
  if (asChild || !href) {
    // the NavLink-only props never reach the dom
    const { active: _active, current: _current, external: _external, disabled, ...rest } = props;
    void _active;
    void _current;
    void _external;
    if (asChild) {
      return (
        <Slot data-slot="breadcrumb-link" data-disabled={disabled ? "" : undefined} className={classes} {...(rest as ComponentProps<typeof Slot>)}>
          {children}
        </Slot>
      );
    }
    return (
      <button
        type="button"
        data-slot="breadcrumb-link"
        disabled={disabled}
        data-disabled={disabled ? "" : undefined}
        className={classes}
        {...(rest as unknown as ComponentProps<"button">)}
      >
        {children}
      </button>
    );
  }
  return (
    <NavLink data-slot="breadcrumb-link" href={href} component={linkComponent ?? context.linkComponent} className={classes} {...props}>
      {children}
    </NavLink>
  );
}

export type BreadcrumbPageProps = ComponentProps<"span">;

function BreadcrumbPageRender({ className, ...props }: BreadcrumbPageProps) {
  const { size, classNames } = useContext(BreadcrumbContext);
  return <span data-slot="breadcrumb-page" aria-current="page" className={cn(PAGE, INNER_GAP[size], classNames?.page, className)} {...props} />;
}

export type BreadcrumbSeparatorProps = ComponentProps<"li"> & {
  // default: the Breadcrumb's separator (chevron). children replace the glyph
  kind?: BreadcrumbSeparatorKind;
};

function BreadcrumbSeparatorRender({ kind, className, children, ...props }: BreadcrumbSeparatorProps) {
  const context = useContext(BreadcrumbContext);
  return (
    <li data-slot="breadcrumb-separator" role="presentation" aria-hidden className={cn(SEPARATOR, context.classNames?.separator, className)} {...props}>
      {children ?? <SeparatorGlyph kind={kind ?? context.separator} />}
    </li>
  );
}

// the separator inside a data-driven crumb: it travels with its crumb when the crumb folds into the menu
function InlineSeparator() {
  const context = useContext(BreadcrumbContext);
  return (
    <span data-slot="breadcrumb-separator" role="presentation" aria-hidden className={cn(SEPARATOR, context.classNames?.separator)}>
      <SeparatorGlyph kind={context.separator} />
    </span>
  );
}

// a menu row that navigates (NavLink) or runs onSelect
function EntryMenuItem({
  label,
  icon,
  href,
  onSelect,
  disabled,
  external,
  current,
  badge,
  textValue,
  description,
}: {
  label: ReactNode;
  icon?: ReactNode;
  href?: string;
  onSelect?: (event: MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  external?: boolean;
  current?: boolean;
  badge?: ReactNode;
  textValue?: string;
  description?: ReactNode;
}) {
  const { linkComponent } = useContext(BreadcrumbContext);
  const trailing = current ? <Check aria-hidden className="size-4 text-foreground" /> : badge;
  if (href && !disabled) {
    return (
      <DropdownMenuItem asChild icon={icon} badge={trailing} description={description} textValue={textValue}>
        <NavLink href={href} component={linkComponent} external={external} active={current} onClick={onSelect}>
          {label}
        </NavLink>
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem
      icon={icon}
      badge={trailing}
      description={description}
      textValue={textValue}
      disabled={disabled || !onSelect}
      onSelect={onSelect ? (event) => onSelect(event as unknown as MouseEvent<HTMLElement>) : undefined}
    >
      {label}
    </DropdownMenuItem>
  );
}

export type BreadcrumbEllipsisProps = Omit<ComponentProps<"button">, "children"> & {
  // the folded entries; without them it renders the bare "…" glyph (a hand-built trail's placeholder)
  items?: BreadcrumbEntry[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // an action sheet below 768px (default: the Breadcrumb's responsive)
  responsive?: boolean;
};

function BreadcrumbEllipsisRender({ items, open, onOpenChange, responsive, className, ...props }: BreadcrumbEllipsisProps) {
  const { size, labels, classNames, responsive: sheets } = useContext(BreadcrumbContext);
  if (!items?.length) {
    return (
      <span data-slot="breadcrumb-ellipsis" role="presentation" aria-hidden className={cn("flex size-6 items-center justify-center", classNames?.ellipsis, className)}>
        <MoreHorizontal className="size-4" />
      </span>
    );
  }
  const keys = entryKeys(items);
  return (
    <DropdownMenuRoot open={open} onOpenChange={onOpenChange} responsive={responsive ?? sheets}>
      <DropdownMenuTrigger asChild>
        <Button
          data-slot="breadcrumb-ellipsis"
          variant="ghost"
          size="icon-sm"
          radius="md"
          aria-label={labels.more}
          className={cn("text-muted-foreground hover:text-foreground data-[state=open]:bg-foreground/10 data-[state=open]:text-foreground", ELLIPSIS_SIZES[size], classNames?.ellipsis, className)}
          {...props}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sheetTitle={labels.more} cancelLabel={labels.cancel} minWidth={200}>
        {items.map((entry, i) => (
          <EntryMenuItem
            key={keys[i]}
            label={filled(entry.label) ? entry.label : entryText(entry)}
            icon={entry.icon}
            href={entry.href}
            onSelect={entry.onSelect}
            textValue={entryText(entry) || undefined}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}

// the chevron after a crumb that opens its siblings
function SiblingSwitcher({ entry, trailKey }: { entry: BreadcrumbEntry; trailKey: string }) {
  const { labels, responsive } = useContext(BreadcrumbContext);
  const [open, setOpen] = useTrailMenu(trailKey);
  const text = entryText(entry);
  const menu = entry.menu ?? [];
  const isCurrent = (item: NavItem, i: number) =>
    (entry.id !== undefined && navItemKey(item, i) === entry.id) ||
    (Boolean(item.href) && Boolean(entry.href) && normalizePath(item.href!) === normalizePath(entry.href!)) ||
    (!entry.href && Boolean(text) && navItemText(item) === text);
  return (
    <DropdownMenuRoot open={open} onOpenChange={setOpen} responsive={responsive}>
      <DropdownMenuTrigger asChild>
        <button type="button" data-slot="breadcrumb-menu" aria-label={labels.siblings(text)} className={cn(SWITCHER, FOCUS)}>
          <ChevronsUpDown aria-hidden className="size-3" />
        </button>
      </DropdownMenuTrigger>
      {/* the phone sheet shows the title: a bare list of names needs its context */}
      <DropdownMenuContent align="start" title={text || undefined} sheetTitle={labels.siblings(text)} cancelLabel={labels.cancel} minWidth={220}>
        {menu.map((item, i) => (
          <EntryMenuItem
            key={navItemKey(item, i)}
            label={item.label}
            icon={item.icon}
            href={item.href}
            onSelect={item.onSelect}
            disabled={item.disabled}
            external={item.external}
            current={isCurrent(item, i)}
            badge={typeof item.badge === "number" ? String(item.badge) : item.badge}
            description={item.description}
            textValue={navItemText(item) || undefined}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}

// the content of one crumb: a link, a button, plain text or the current page, plus the sibling switcher
function Crumb({
  entry,
  current,
  maxItemWidth,
  trailKey,
  compact = false,
}: {
  entry: BreadcrumbEntry;
  current: boolean;
  maxItemWidth: number;
  trailKey: string;
  // just the icon when there is one (the first crumb of the phone trail)
  compact?: boolean;
}) {
  const { size } = useContext(BreadcrumbContext);
  const text = entryText(entry);
  const iconOnly = filled(entry.icon) && (compact || !filled(entry.label));
  const inner = (
    <>
      {filled(entry.icon) && (
        <span data-slot="breadcrumb-icon" aria-hidden className={ICON}>
          {entry.icon}
        </span>
      )}
      {!iconOnly ? (
        <span data-slot="breadcrumb-label" className={LABEL} style={{ maxWidth: maxItemWidth }}>
          {entry.label}
        </span>
      ) : text ? (
        // an icon-only crumb keeps its name as text: aria-label on a plain span is not read everywhere
        <span className="sr-only">{text}</span>
      ) : null}
    </>
  );

  // an icon alone is ~22px wide: widen its touch area to 44px (the gap and separator have room for it)
  const hit = iconOnly ? "pointer-coarse:after:-inset-x-3" : undefined;
  let node: ReactNode;
  if (current) {
    node = (
      <BreadcrumbPage>
        {inner}
      </BreadcrumbPage>
    );
  } else if (entry.href) {
    node = (
      <BreadcrumbLink href={entry.href} onClick={entry.onSelect} className={hit}>
        {inner}
      </BreadcrumbLink>
    );
  } else if (entry.onSelect) {
    node = (
      <BreadcrumbLink onClick={entry.onSelect as NavLinkProps["onClick"]} className={hit}>
        {inner}
      </BreadcrumbLink>
    );
  } else {
    // a level without its own page: plain text
    node = (
      <span data-slot="breadcrumb-text" className={cn("inline-flex min-w-0 items-center", INNER_GAP[size])}>
        {inner}
      </span>
    );
  }

  return (
    <>
      {text ? (
        // icon-only crumbs always name themselves; text crumbs only when cut off
        <Tooltip content={text} overflowOnly={!iconOnly}>
          {node}
        </Tooltip>
      ) : (
        node
      )}
      {entry.menu?.length ? <SiblingSwitcher entry={entry} trailKey={trailKey} /> : null}
    </>
  );
}

// ---------------------------------------------------------------- the trail

type Unit = { kind: "entry"; index: number } | { kind: "more" };

type RowItemProps = Partial<OverflowItemProps> & { className?: string; style?: CSSProperties; children?: ReactNode };

function useItemMotion(animate: boolean) {
  const reduced = usePrefersReducedMotion();
  if (!animate) return null;
  const initial: TargetAndTransition = reduced ? { opacity: 0 } : { opacity: 0, x: 8 };
  return {
    initial,
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, transition: { duration: durations.fast } },
    transition: reduced ? { duration: durations.fast } : springs.smooth,
    slide: !reduced,
  };
}

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

// when the trail changes, crumbs that stay but moved (a sibling with a longer name, a fold) glide from where they
// were. measured against the row (offsetLeft), so a page that changes height around the trail never drags them;
// css translate, so it composes with motion's transform on the same element
function useSlideCrumbs(list: { current: HTMLOListElement | null }, trailKey: string, enabled: boolean) {
  const last = useRef<{ key: string; x: Map<string, number> } | null>(null);
  useIsomorphicLayoutEffect(() => {
    const row = list.current;
    if (!row) return;
    const now = new Map<string, number>();
    for (const el of Array.from(row.children) as HTMLElement[]) {
      const key = el.dataset.key;
      if (key && el.dataset.overflow !== "hidden") now.set(key, el.offsetLeft);
    }
    const before = last.current;
    if (enabled && before && before.key !== trailKey && typeof row.animate === "function") {
      const easing = getComputedStyle(row).getPropertyValue("--motion-ease-spring").trim() || "cubic-bezier(0.22, 1, 0.36, 1)";
      for (const el of Array.from(row.children) as HTMLElement[]) {
        const key = el.dataset.key;
        const from = key ? before.x.get(key) : undefined;
        const to = key ? now.get(key) : undefined;
        if (from === undefined || to === undefined || Math.abs(from - to) < 1) continue;
        el.animate([{ translate: `${from - to}px 0` }, { translate: "0 0" }], { duration: 500, easing });
      }
    }
    last.current = { key: trailKey, x: now };
  });
}

// the desktop trail: every entry, folding the middle into "…" while it does not fit or past maxItems
function Trail({
  entries,
  collapse,
  maxItems,
  itemsBeforeCollapse,
  itemsAfterCollapse,
  maxItemWidth,
  animate,
  className,
}: {
  entries: BreadcrumbEntry[];
  collapse: BreadcrumbCollapse;
  maxItems: number;
  itemsBeforeCollapse: number;
  itemsAfterCollapse: number;
  maxItemWidth: number;
  animate: boolean;
  className?: string;
}) {
  const context = useContext(BreadcrumbContext);
  const count = entries.length;
  const keys = entryKeys(entries);
  const trailKey = keys.join("|");
  const [menuOpen, setMenuOpen] = useTrailMenu(trailKey);
  const itemMotion = useItemMotion(animate);
  const list = useRef<HTMLOListElement | null>(null);
  useSlideCrumbs(list, trailKey, Boolean(itemMotion?.slide));

  // folding by count: keep `before` at the start and `after` at the end; ignored when it would not fold anything
  const before = Math.max(1, Math.floor(itemsBeforeCollapse) || 1);
  const after = Math.max(1, Math.floor(itemsAfterCollapse) || 1);
  const byCount = maxItems > 0 && count > maxItems && maxItems >= before + after + 1 && count > before + after;
  const forced = byCount ? Array.from({ length: count - before - after }, (_, i) => before + i) : [];

  // what the overflow hook measures: with a count fold the "…" is a unit of its own (always shown, pinned)
  const units: Unit[] = byCount
    ? [
        ...Array.from({ length: before }, (_, i): Unit => ({ kind: "entry", index: i })),
        { kind: "more" },
        ...Array.from({ length: after }, (_, i): Unit => ({ kind: "entry", index: count - after + i })),
      ]
    : entries.map((_, index): Unit => ({ kind: "entry", index }));
  const moreUnit = byCount ? before : -1;
  const pinned = units.length > 1 ? [0, units.length - 1, ...(byCount ? [moreUnit] : [])] : [0];

  const { containerRef, getItemProps, getMoreProps, hiddenIndexes } = useOverflowItems({
    count: units.length,
    collapseOrder: "middle",
    pinned,
    disabled: collapse === "never" || count < 3,
  });
  const rowRef = useCallback(
    (el: HTMLOListElement | null) => {
      list.current = el;
      return containerRef(el);
    },
    [containerRef],
  );

  const hiddenEntries = new Set(forced);
  for (const u of hiddenIndexes) {
    const unit = units[u];
    if (unit?.kind === "entry") hiddenEntries.add(unit.index);
  }
  const folded = Array.from(hiddenEntries).sort((a, b) => a - b);
  // with every middle entry folded and still no room, the current page truncates. it only shrinks then, so the
  // shrunk width fills the row exactly and never tricks the overflow hook into unfolding
  const foldable = units.length - new Set(pinned).size;
  const squeeze = collapse === "auto" && hiddenIndexes.length >= foldable;
  const middle = Array.from({ length: Math.max(0, count - 2) }, (_, i) => i + 1);

  const renderItem = (key: string, props: RowItemProps, content: ReactNode, grow = false) => {
    const { className: extra, style, ...rest } = props;
    const shared = {
      "data-slot": "breadcrumb-item",
      className: cn(
        "inline-flex shrink-0 items-center whitespace-nowrap",
        ITEM_SIZES[context.size],
        grow && "max-w-[70%]",
        grow && squeeze && "min-w-0 shrink",
        context.classNames?.item,
        extra,
      ),
      style,
      ...rest,
    };
    if (itemMotion) {
      const { initial, animate: target, exit, transition } = itemMotion;
      return (
        <motion.li key={key} data-key={key} {...shared} initial={initial} animate={target} exit={exit} transition={transition}>
          {content}
        </motion.li>
      );
    }
    return (
      <li key={key} data-key={key} {...shared}>
        {content}
      </li>
    );
  };

  const moreContent = (
    <>
      <InlineSeparator />
      {/* while nothing is folded it stays measured (hidden) with the middle entries, so its width never changes */}
      <BreadcrumbEllipsis items={(folded.length ? folded : middle).map((i) => entries[i])} open={menuOpen} onOpenChange={setMenuOpen} />
    </>
  );

  const rows: ReactNode[] = [];
  units.forEach((unit, u) => {
    if (unit.kind === "more") {
      rows.push(renderItem("__more", getItemProps(u), moreContent));
      return;
    }
    const i = unit.index;
    const current = i === count - 1;
    rows.push(
      renderItem(
        keys[i],
        getItemProps(u),
        <>
          {i > 0 && <InlineSeparator />}
          <Crumb entry={entries[i]} current={current} maxItemWidth={maxItemWidth} trailKey={trailKey} />
        </>,
        current,
      ),
    );
    // folding by width: the "…" sits right after the first crumb, where the folded ones were
    if (!byCount && u === 0 && count > 2) rows.push(renderItem("__more", getMoreProps(), moreContent));
  });

  const listProps = {
    "data-slot": "breadcrumb-list",
    "data-collapse": collapse,
    "data-folded": folded.length || undefined,
    className: trailListClass(context, collapse === "auto", className),
  };

  if (!itemMotion) {
    return (
      <ol ref={rowRef} {...listProps}>
        {rows}
      </ol>
    );
  }
  return (
    <ol ref={rowRef} {...listProps}>
      <AnimatePresence initial={false} mode="popLayout">
        {rows}
      </AnimatePresence>
    </ol>
  );
}

// phones, collapse: first, "…", current
function CompactTrail({ entries, maxItemWidth, className }: { entries: BreadcrumbEntry[]; maxItemWidth: number; className?: string }) {
  const count = entries.length;
  const keys = entryKeys(entries);
  const trailKey = keys.join("|");
  const [menuOpen, setMenuOpen] = useTrailMenu(trailKey);
  const middle = entries.slice(1, -1);
  return (
    <BreadcrumbList className={cn("flex-nowrap", className)}>
      {count > 1 && (
        <BreadcrumbItem className="min-w-0 shrink-[4] whitespace-nowrap">
          <Crumb entry={entries[0]} current={false} maxItemWidth={Math.min(maxItemWidth, 120)} trailKey={trailKey} compact />
        </BreadcrumbItem>
      )}
      {middle.length > 0 && (
        <BreadcrumbItem className="shrink-0">
          <InlineSeparator />
          <BreadcrumbEllipsis items={middle} open={menuOpen} onOpenChange={setMenuOpen} />
        </BreadcrumbItem>
      )}
      <BreadcrumbItem className="min-w-0 whitespace-nowrap">
        {count > 1 && <InlineSeparator />}
        <Crumb entry={entries[count - 1]} current maxItemWidth={maxItemWidth} trailKey={trailKey} />
      </BreadcrumbItem>
    </BreadcrumbList>
  );
}

// phones, back: one "‹ Parent" link
function BackLink({ entry, className }: { entry: BreadcrumbEntry; className?: string }) {
  const { labels, linkComponent, classNames } = useContext(BreadcrumbContext);
  const text = entryText(entry);
  return (
    <NavLink
      data-slot="breadcrumb-back"
      href={entry.href!}
      component={linkComponent}
      onClick={entry.onSelect}
      aria-label={text ? labels.back(text) : undefined}
      className={cn(BACK, FOCUS, classNames?.back, className)}
    >
      <ChevronLeft aria-hidden className="-ms-1 size-[18px] shrink-0 rtl:rotate-180" />
      <span className="min-w-0 truncate">{filled(entry.label) ? entry.label : text}</span>
    </NavLink>
  );
}

// ---------------------------------------------------------------- Breadcrumb

export type BreadcrumbProps = Omit<ComponentProps<"nav">, "children"> & {
  // the trail, root first; the last entry is the current page. leave empty to build the trail from the parts
  items?: BreadcrumbEntry[];
  // chevron, slash, dot, arrow or any node
  separator?: BreadcrumbSeparatorKind | ReactNode;
  // 13 / 14px text, 4 / 6px gaps
  size?: BreadcrumbSize;
  // auto: fold the middle into "…" while the row does not fit. never: wrap onto more lines
  collapse?: BreadcrumbCollapse;
  // also fold when there are more entries than this (0: only when they do not fit)
  maxItems?: number;
  // entries kept at the start when folding by maxItems
  itemsBeforeCollapse?: number;
  // entries kept at the end (parent + current)
  itemsAfterCollapse?: number;
  // px; longer labels truncate and show the full text in a tooltip
  maxItemWidth?: number;
  // below the mobile breakpoint. back: one "‹ Parent" link. collapse: first, "…", current. full: the desktop trail
  mobileVariant?: BreadcrumbMobileVariant;
  // where the phone form takes over: sm (640px), md (768px), or always (narrow side panels)
  mobileBreakpoint?: BreadcrumbMobileBreakpoint;
  // entering entries slide in when the trail changes
  animate?: boolean;
  // the "…" and sibling menus open as bottom action sheets below 768px
  responsive?: boolean;
  // the router link for every crumb (default: the LinkProvider's)
  linkComponent?: LinkComponent;
  labels?: BreadcrumbLabels;
  classNames?: Partial<Record<BreadcrumbSlot, string>>;
  // a hand-built trail: BreadcrumbList with items, links, pages and separators
  children?: ReactNode;
};

function BreadcrumbRender(breadcrumbProps: BreadcrumbProps) {
  const {
    items,
    separator = "chevron",
    size = "md",
    collapse = "auto",
    maxItems = 0,
    itemsBeforeCollapse = 1,
    itemsAfterCollapse = 2,
    maxItemWidth = 200,
    mobileVariant = "back",
    mobileBreakpoint = "sm",
    animate = true,
    responsive = true,
    linkComponent,
    labels,
    classNames,
    className,
    children,
    ...props
  } = useDefaults("breadcrumb", breadcrumbProps);
  const sizeKey = safeSize(size);
  const merged: Required<BreadcrumbLabels> = { ...DEFAULT_LABELS, ...labels };
  const value: BreadcrumbContextValue = {
    size: sizeKey,
    separator: isValidElement(separator) || typeof separator === "string" ? separator : "chevron",
    linkComponent,
    labels: merged,
    classNames,
    responsive,
  };
  const entries = items ?? [];
  const count = entries.length;
  const width = Number.isFinite(maxItemWidth) && maxItemWidth > 0 ? maxItemWidth : 200;
  const safeCollapse: BreadcrumbCollapse = collapse === "never" ? "never" : "auto";
  const variant: BreadcrumbMobileVariant = mobileVariant === "collapse" || mobileVariant === "full" ? mobileVariant : "back";
  const parent = count > 1 ? entries[count - 2] : undefined;
  // the back link needs a parent with a page; otherwise the phone shows the collapsed trail
  const phone = variant === "back" && !parent?.href ? "collapse" : variant;
  const breakpoint = BREAKPOINTS[mobileBreakpoint] ?? BREAKPOINTS.sm;

  return (
    <BreadcrumbContext.Provider value={value}>
      <nav
        data-slot="breadcrumb"
        aria-label={merged.navigation}
        data-size={sizeKey}
        data-mobile-variant={phone}
        className={cn("min-w-0", className)}
        {...props}
      >
        {items ? (
          count === 0 ? null : (
            <>
              <Trail
                entries={entries}
                collapse={safeCollapse}
                maxItems={Number.isFinite(maxItems) ? maxItems : 0}
                itemsBeforeCollapse={itemsBeforeCollapse}
                itemsAfterCollapse={itemsAfterCollapse}
                maxItemWidth={width}
                animate={animate}
                className={phone === "full" ? undefined : breakpoint.trail}
              />
              {phone === "back" && parent?.href ? (
                <div className={cn("flex min-w-0", breakpoint.phone)}>
                  <BackLink entry={parent} />
                </div>
              ) : phone === "collapse" ? (
                <CompactTrail entries={entries} maxItemWidth={width} className={breakpoint.phone} />
              ) : null}
            </>
          )
        ) : (
          children
        )}
      </nav>
    </BreadcrumbContext.Provider>
  );
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const BreadcrumbList = withRef(BreadcrumbListRender, "BreadcrumbList");
export const BreadcrumbItem = withRef(BreadcrumbItemRender, "BreadcrumbItem");
export const BreadcrumbLink = withRef(BreadcrumbLinkRender, "BreadcrumbLink");
export const BreadcrumbPage = withRef(BreadcrumbPageRender, "BreadcrumbPage");
export const BreadcrumbSeparator = withRef(BreadcrumbSeparatorRender, "BreadcrumbSeparator");
export const BreadcrumbEllipsis = withRef(BreadcrumbEllipsisRender, "BreadcrumbEllipsis");
export const Breadcrumb = withRef(BreadcrumbRender, "Breadcrumb");
